import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card, Row, Col, Radio, Input, Select, InputNumber, Switch, Button, App,
  Empty, Tag, Space, Typography, Tooltip, Upload, Badge, Modal, Collapse, Image,
} from 'antd';
import {
  VideoCameraOutlined, DownloadOutlined, ClockCircleOutlined, HistoryOutlined,
  DeleteOutlined, ReloadOutlined, EyeOutlined, PlayCircleOutlined,
  CheckCircleOutlined, CloseCircleOutlined, SyncOutlined, PlusOutlined,
  PictureOutlined, SoundOutlined, ExclamationCircleOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import api, { multimodalAPI, getFullApiUrl } from '../services/api';
import { t } from '../utils/i18n';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

// 终态集合：需同时覆盖后端记录状态（completed/active）与接口返回状态（succeeded）
const TERMINAL = new Set([
  'succeeded', 'completed', 'success', 'active',
  'failed', 'error', 'expired', 'cancelled', 'canceled', 'timeout',
]);

// 状态配置（与灵感创作一致）
const getStatusConfig = () => ({
  queued: { color: 'processing', icon: <ClockCircleOutlined />, text: t('queueing') },
  pending: { color: 'processing', icon: <ClockCircleOutlined />, text: t('queueing') },
  processing: { color: 'processing', icon: <SyncOutlined spin />, text: t('generating_progress') },
  running: { color: 'processing', icon: <SyncOutlined spin />, text: t('generating_progress') },
  completed: { color: 'success', icon: <CheckCircleOutlined />, text: t('completed') },
  succeeded: { color: 'success', icon: <CheckCircleOutlined />, text: t('completed') },
  success: { color: 'success', icon: <CheckCircleOutlined />, text: t('completed') },
  failed: { color: 'error', icon: <CloseCircleOutlined />, text: t('failed') },
  error: { color: 'error', icon: <CloseCircleOutlined />, text: t('failed') },
  cancelled: { color: 'default', icon: <CloseCircleOutlined />, text: t('canceled') },
  expired: { color: 'default', icon: <ClockCircleOutlined />, text: t('expired') },
  timeout: { color: 'warning', icon: <ClockCircleOutlined />, text: '超时' },
});

// 档位颜色（W3 metadata tier → 颜色）
const TIER_COLORS = { Standard: '#1677ff', Pro: '#722ed1', Prime: '#fa8c16', Lite: '#52c41a', Fast: '#52c41a' };

// 两个系列共用本页面：W3.0（上游 Muleroute/Berry）与 WAN3.0（上游 阿里云百炼 DashScope）
// 差异集中在三处：模型名前缀、功能开关 key、请求体格式（见 buildRequestBody）。
const VARIANTS = {
  w3: { prefix: 'W3.0', feature: 'w3_studio_enabled', labelKey: 'w3_studio', defaultResolution: '1080p' },
  wan3: { prefix: 'WAN3.0', feature: 'wan3_studio_enabled', labelKey: 'wan3_studio', defaultResolution: '1080P' },
};

// buildRequestBody 按系列拼装请求体。
// W3.0 用平台扁平格式；WAN3.0 透传百炼原生格式（input.media[] + parameters{}），
// 客户自百炼迁移时只需改 base_url 与 api_key。
const buildRequestBody = ({ variant, model, prompt, mode, resolution, ratio,
  duration, smartDuration, audio, promptExtend, seed, firstFrame, lastFrame,
  refImages, refVideos, refAudios }) => {
  // 上游 seed 只接受 >=0；随机（-1）时不传该字段，由上游随机
  const seedVal = parseInt(seed, 10);
  const hasSeed = !isNaN(seedVal) && seedVal >= 0;

  if (variant === 'wan3') {
    const media = [];
    if (mode === 'keyframe') {
      if (firstFrame) media.push({ type: 'first_frame', url: firstFrame.url });
      if (lastFrame) media.push({ type: 'last_frame', url: lastFrame.url });
    }
    if (mode === 'reference') {
      refImages.forEach(f => media.push({ type: 'reference_image', url: f.url }));
      refVideos.forEach(f => media.push({ type: 'reference_video', url: f.url }));
      refAudios.forEach(f => media.push({ type: 'reference_audio', url: f.url }));
    }
    const parameters = {
      resolution,
      ratio,
      duration: smartDuration ? -1 : duration,
      audio,
      prompt_extend: promptExtend,
    };
    if (hasSeed) parameters.seed = seedVal;
    const input = { prompt };
    if (media.length) input.media = media;
    return { model, input, parameters };
  }

  const body = {
    model,
    prompt,
    resolution,
    ratio,
    duration: smartDuration ? -1 : duration,
    audio,
    prompt_extend: promptExtend,
  };
  if (hasSeed) body.seed = seedVal;
  if (mode === 'keyframe') {
    if (firstFrame) body.first_frame = firstFrame.url;
    if (lastFrame) body.last_frame = lastFrame.url;
  }
  if (mode === 'reference') {
    if (refImages.length) body.reference_images = refImages.map(f => f.url);
    if (refVideos.length) body.reference_videos = refVideos.map(f => f.url);
    if (refAudios.length) body.reference_audios = refAudios.map(f => f.url);
  }
  return body;
};

const formatTimeShort = (isoStr) => {
  try {
    const d = new Date(isoStr);
    const pad = n => String(n).padStart(2, '0');
    return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return isoStr || '-'; }
};

// 解析后端返回的 JSON 字符串数组（参考素材）
const parseRefList = (val) => {
  if (!val || val === 'null') return [];
  try {
    const parsed = typeof val === 'string' ? JSON.parse(val) : val;
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch { return []; }
};

// 解析生成参数快照
const parseRefObj = (val) => {
  if (!val) return null;
  try {
    return typeof val === 'string' ? JSON.parse(val) : val;
  } catch { return null; }
};

// 上游报错原样展示，不做改写。兼容上游的两种错误形态：
//   创建期非 2xx：{"status":400,"title":"...","detail":"...","error_code":2001}
//   任务级失败：  {"task_info":{"error":{"code":4002,"title":"...","detail":"..."}}}
// 取不到时才由调用方回退到通用文案。
const upstreamErrorText = (body) => {
  if (!body || typeof body !== 'object') return '';
  const pick = (e) => {
    if (!e) return '';
    if (typeof e === 'string') return e;
    const title = e.title || '';
    const detail = e.detail || e.message || '';
    if (title && detail) return `${title}: ${detail}`;
    return title || detail || '';
  };
  // pick(body) 兜底覆盖创建期形态（title / detail 直接挂在顶层）
  return pick(body.task_info?.error) || pick(body.error) || pick(body) || '';
};

const isDownloadableUrl = (url) => url && (url.startsWith('http://') || url.startsWith('https://'));
const getFileExt = (url) => {
  try {
    const p = new URL(url).pathname;
    const m = p.match(/\.([a-zA-Z0-9]{2,5})(?:\?|$)/);
    return m ? m[1] : 'mp4';
  } catch { return 'mp4'; }
};

const W3Studio = ({ variant = 'w3' }) => {
  const { message } = App.useApp();
  const { prefix: MODEL_PREFIX, feature: FEATURE_KEY, labelKey: LABEL_KEY,
    defaultResolution: DEFAULT_RESOLUTION } = VARIANTS[variant] || VARIANTS.w3;
  const isWan3 = variant === 'wan3';
  // 功能开关
  const [enabled, setEnabled] = useState(null); // null=加载中
  // 模型
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [meta, setMeta] = useState(null);
  // 表单
  const [mode, setMode] = useState('text'); // text | keyframe | reference
  const [prompt, setPrompt] = useState('');
  const [firstFrame, setFirstFrame] = useState(null); // {url,name}
  const [lastFrame, setLastFrame] = useState(null);
  const [refImages, setRefImages] = useState([]); // [{url,name,type}]
  const [refVideos, setRefVideos] = useState([]);
  const [refAudios, setRefAudios] = useState([]);
  const [resolution, setResolution] = useState(DEFAULT_RESOLUTION);
  const [ratio, setRatio] = useState('adaptive');
  const [duration, setDuration] = useState(5);
  const [smartDuration, setSmartDuration] = useState(false);
  const [audio, setAudio] = useState(true);
  const [promptExtend, setPromptExtend] = useState(true);
  const [seed, setSeed] = useState(-1);
  // 历史
  const [history, setHistory] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const pollTimersRef = useRef({});
  const historyRef = useRef({});
  const notifiedRef = useRef(new Set()); // 已提示过的任务，避免重复弹窗

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/features');
        setEnabled(!!(res?.data?.[FEATURE_KEY]));
      } catch {
        setEnabled(false);
      }
    })();
    fetchModels();
    refreshHistory();
    return () => Object.values(pollTimersRef.current).forEach(clearTimeout);
  }, []);

  const fetchModels = async () => {
    try {
      const res = await api.get('/models');
      const list = res?.data?.list || res?.data || [];
      const list2 = list.filter(m => (m.model_name || m.name || '').startsWith(MODEL_PREFIX) && m.type === 'video');
      setModels(list2);
      if (list2.length > 0 && !selectedModel) {
        setSelectedModel(list2[0].model_name || list2[0].name);
      }
    } catch {
      message.warning('模型列表加载失败');
    }
  };

  // 选中模型变化时同步 metadata
  useEffect(() => {
    const m = models.find(x => (x.model_name || x.name) === selectedModel);
    if (!m) { setMeta(null); return; }
    let parsed = {};
    try { parsed = JSON.parse(m.metadata || '{}'); } catch { parsed = {}; }
    setMeta(parsed);
    if (parsed.resolutions?.length) setResolution(parsed.resolutions[0].value || DEFAULT_RESOLUTION);
    setDuration(parsed.durationRange?.[0] || 5);
  }, [selectedModel, models]);

  // 预估费用：按「单价($/秒) × 时长」预扣；智能时长按 30 秒预扣、完成后按实际结算
  const pricePerSecond = meta?.resolution_pricing?.[resolution] ?? null;
  const estimateSeconds = smartDuration ? 30 : duration;
  const estimateCost = pricePerSecond != null ? (pricePerSecond * estimateSeconds).toFixed(3) : null;

  // ========== 历史记录（参考灵感创作：服务器记录 + 本地临时记录合并） ==========
  const refreshHistory = useCallback(async () => {
    try {
      // 取全部历史（服务端按模型前缀过滤，且不再按 24h 过期过滤）
      const res = await multimodalAPI.getAllVideoHistory(MODEL_PREFIX);
      const items = (res.items || []).map(item => {
        let status = item.status || 'succeeded';
        if (item.expired) status = 'expired';
        return {
          id: `srv-${item.id}`,
          type: 'video',
          model: item.model,
          prompt: item.prompt,
          taskId: item.task_id || null,
          status,
          errorMsg: item.error_msg || item.storage_error || '',
          videoUrl: item.video_url || '',
          duration: item.duration || 0,
          resolution: item.resolution,
          refImages: parseRefList(item.reference_images),
          refVideos: parseRefList(item.reference_videos),
          refAudios: parseRefList(item.reference_audio),
          firstFrame: item.first_frame || '',
          lastFrame: item.last_frame || '',
          params: parseRefObj(item.params),
          createdAt: item.created_at,
          expired: item.expired,
          storageStatus: item.storage_status || '',
          manualTransferCharge: Number(item.manual_transfer_charge || 0),
          manualTransferAt: item.manual_transfer_at || '',
          source: 'server',
          serverIds: [item.id],
        };
      });
      // 触顶截断时给出明确提示，不做静默截断
      if (res.truncated) {
        message.warning(`历史记录较多，当前仅加载最近 ${items.length} 条（共 ${res.total} 条）`);
      }
      // 只保留服务器数据，本地临时记录在与服务器 taskId 一致时被替换
      // 注意：本地已到终态的记录不能被服务器数据降级为进行中，否则会触发重复轮询与重复提示
      if (items.length > 0) {
        setHistory(prev => {
          const serverKeys = new Set(items.map(s => s.taskId).filter(Boolean));
          const localOnly = prev.filter(p => p.source === 'local' && !serverKeys.has(p.taskId));
          const localTerminal = new Map(
            prev.filter(p => p.taskId && TERMINAL.has(p.status)).map(p => [p.taskId, p])
          );
          const isSuccessStatus = (st) => ['succeeded', 'completed', 'success'].includes(st);
          const merged = [
            ...items.map(s => {
              const local = localTerminal.get(s.taskId);
              if (!local) return s;
              // 管理员二次转储成功后，服务器会从 failed/expired 变成 completed；
              // 这是允许的升级，不能因为本地终态就永远显示失败。
              if (isSuccessStatus(s.status) && !isSuccessStatus(local.status)) {
                return { ...s, videoUrl: s.videoUrl || local.videoUrl };
              }
              return { ...s, status: local.status, videoUrl: s.videoUrl || local.videoUrl };
            }),
            ...localOnly,
          ];
          merged.sort((a, b) =>
            new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
          );
          return merged;
        });
      }
    } catch { /* ignore */ }
  }, []);

  // 引用当前 history 供轮询判断终态（避免闭包过期）
  useEffect(() => {
    historyRef.current = Object.fromEntries(history.map(h => [h.taskId, h]));
  }, [history]);

  // 管理员二次转储成功后，给用户一次明确的扣费提示（每条记录只提示一次）
  useEffect(() => {
    if (isWan3) return;
    const noticed = history.filter(h =>
      Number(h.manualTransferCharge) > 0 &&
      h.manualTransferAt &&
      !localStorage.getItem(`w3_manual_transfer_seen:${h.serverIds?.[0] || h.id}:${h.manualTransferAt}`)
    );
    if (!noticed.length) return;
    noticed.forEach(h => {
      localStorage.setItem(`w3_manual_transfer_seen:${h.serverIds?.[0] || h.id}:${h.manualTransferAt}`, '1');
    });
    const amount = noticed.reduce((sum, h) => sum + Number(h.manualTransferCharge || 0), 0);
    Modal.warning({
      title: t('w3_manual_transfer_notice_title'),
      content: t('w3_manual_transfer_notice_body').replace('{amount}', amount.toFixed(3)),
      okText: t('w3_manual_transfer_ok'),
    });
  }, [history, isWan3]);

  // 页面加载/刷新后自动恢复对进行中任务的轮询
  useEffect(() => {
    history.forEach(item => {
      if (!item.taskId || TERMINAL.has(item.status)) return;
      if (pollTimersRef.current[item.taskId]) return;
      pollTask(item);
    });
  }, [history]);

  const pollTask = useCallback((record) => {
    const key = record.taskId;
    if (!key || pollTimersRef.current[key]) return;
    let attempts = 0;
    let delay = 5000; // 正常间隔 5s；出错时退避，避免撞限流后持续打接口
    const tick = () => {
      if (attempts >= 480) { // 40 分钟上限（含退避）
        setHistory(prev => prev.map(h => h.taskId === key ? { ...h, status: 'timeout' } : h));
        return;
      }
      attempts += 1;
      pollTimersRef.current[key] = setTimeout(async () => {
        try {
          const res = await api.get(`/v1/videos/generations/${key}?model=${encodeURIComponent(record.model)}`);
          delay = 5000; // 成功后恢复正常间隔
          const status = res?.status || '';
          const videoUrl = res?.video_url || res?.videos?.[0] || '';
          setHistory(prev => prev.map(h => {
            if (h.taskId !== key) return h;
            const merged = { ...h, videoUrl: videoUrl || h.videoUrl };
            if (TERMINAL.has(h.status)) return merged; // 终态不降级
            merged.status = status || h.status;
            return merged;
          }));
          if (TERMINAL.has(res?.status)) {
            delete pollTimersRef.current[key];
            // 同一任务只提示一次，避免任何重入路径造成提示循环
            if (!notifiedRef.current.has(key)) {
              notifiedRef.current.add(key);
              if (res?.status === 'succeeded') {
                message.success(t('generation_complete_video_msg'));
              } else if (res?.status === 'failed') {
                message.error({ content: t('generation_failed_video') + ': ' + (upstreamErrorText(res) || t('unknown_error')), style: { textAlign: 'left' } });
              }
            }
            refreshHistory(); // 完成后拉取服务器记录（含时长/缩略图）
            return;
          }
          tick();
        } catch (e) {
          const code = e?.response?.status;
          if (code === 401) { delete pollTimersRef.current[key]; return; }
          // 429 / 5xx / 网络错误：指数退避（最长 60s），避免持续冲击接口
          if (code === 429 || code >= 500 || !code) {
            delay = Math.min(delay * 2, 60000);
          }
          tick();
        }
      }, delay);
    };
    tick();
  }, [message, refreshHistory]);

  // ========== 文件上传（与灵感创作一致：multimodalAPI.uploadFile → /files） ==========
  const doUpload = (file, onOk) => {
    multimodalAPI.uploadFile(file)
      .then(res => {
        const upUrl = res.url || res.URL;
        if (upUrl) onOk(upUrl, file);
        else message.error(res.error || t('upload_failed'));
      })
      .catch(e => message.error(e.message || t('upload_failed')));
  };

  // 单图上传（首帧/尾帧）
  const renderImageUpload = (image, setImage, label) => (
    <div style={{ marginBottom: 8 }}>
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>{label}</Text>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {image ? (
          <div style={{ position: 'relative', width: 72, height: 72 }}>
            <img src={image.url || image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, border: '1px solid #e8e8e8' }} />
            <Button size="small" danger type="text" icon={<CloseCircleOutlined />}
              style={{ position: 'absolute', top: -8, right: -8, padding: 0 }}
              onClick={() => setImage(null)} />
          </div>
        ) : (
          <Upload accept="image/*" showUploadList={false}
            beforeUpload={(file) => { doUpload(file, (url, f) => setImage({ url, name: f.name })); return false; }}>
            <div style={{
              width: 72, height: 72, border: '1px dashed #d9d9d9',
              borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#999', fontSize: 11,
            }}>
              <Space direction="vertical" size={0} align="center">
                <PlusOutlined />
                <span style={{ fontSize: 10 }}>{t('upload')}</span>
              </Space>
            </div>
          </Upload>
        )}
      </div>
    </div>
  );

  // 多文件上传（参考图/视频/音频）
  const renderMultiUpload = (files, setFiles, accept, label, maxCount, icon) => (
    <div style={{ marginBottom: 8 }}>
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
        {icon} {label} {maxCount > 0 && <Text type="secondary" style={{ fontSize: 10 }}>{t('max_count', { count: maxCount })}</Text>}
      </Text>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {files.map((f, i) => (
          <div key={i} style={{ position: 'relative', width: 56, height: 56 }}>
            {f.type?.startsWith('video') ? (
              <div style={{ width: '100%', height: '100%', background: '#000', borderRadius: 6, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {f.url ? <video src={f.url} preload="metadata" muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: f.url ? 'rgba(0,0,0,0.25)' : 'transparent' }}>
                  <PlayCircleOutlined style={{ fontSize: 20, color: '#fff' }} />
                </div>
              </div>
            ) : f.type?.startsWith('audio') ? (
              <div style={{ width: '100%', height: '100%', background: '#f0f5ff', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <SoundOutlined style={{ fontSize: 20, color: '#1677ff' }} />
              </div>
            ) : (
              <img src={f.url || f} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, border: '1px solid #e8e8e8' }} />
            )}
            <Text type="secondary" style={{ fontSize: 9, display: 'block', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 56 }}>
              {f.name?.length > 6 ? f.name.substring(0, 6) + '..' : f.name}
            </Text>
            <Button size="small" danger type="text" icon={<CloseCircleOutlined />}
              style={{ position: 'absolute', top: -8, right: -8, padding: 0, fontSize: 10 }}
              onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))} />
          </div>
        ))}
        {(maxCount === 0 || files.length < maxCount) && (
          <Upload accept={accept} showUploadList={false}
            beforeUpload={(file) => {
              doUpload(file, (url, f) => setFiles(prev => [...prev, { url, name: f.name, type: f.type }]));
              return false;
            }}>
            <div style={{
              width: 56, height: 56, border: '1px dashed #d9d9d9',
              borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#999', fontSize: 10,
            }}>
              <PlusOutlined style={{ fontSize: 12 }} />
            </div>
          </Upload>
        )}
      </div>
    </div>
  );

  const handleGenerate = async () => {
    if (!selectedModel) { message.warning(t('select_model_placeholder')); return; }
    if (!prompt.trim()) { message.warning(t('enter_prompt_required')); return; }
    if (mode === 'keyframe' && !firstFrame) { message.warning('请上传首帧图片'); return; }

    setGenerating(true);
    try {
      const body = buildRequestBody({
        variant, model: selectedModel, prompt: prompt.trim(), mode,
        resolution, ratio, duration, smartDuration, audio, promptExtend, seed,
        firstFrame, lastFrame, refImages, refVideos, refAudios,
      });

      const res = await api.post('/v1/videos/generations', body);
      const taskId = res?.id;
      if (!taskId) throw new Error('No task_id returned');

      const record = {
        id: `local-${taskId}`,
        type: 'video',
        model: selectedModel,
        prompt: prompt.trim(),
        taskId,
        status: 'queued',
        errorMsg: '',
        videoUrl: '',
        duration: smartDuration ? -1 : duration,
        resolution,
        createdAt: new Date().toISOString(),
        source: 'local',
        serverIds: [],
      };
      setHistory(prev => [record, ...prev]);
      pollTask(record);
      message.success('任务已提交');
    } catch (e) {
      // 创建期上游报错已由后端原样透传，这里直接展示其原文；取不到才用通用文案
      const reason = upstreamErrorText(e?.response?.data) || e?.serverMessage || '';
      message.error({
        content: t('generation_failed_video') + ': ' + (reason || '请稍后重试'),
        style: { textAlign: 'left' },
      });
      console.error(e);
    } finally {
      setGenerating(false);
    }
  };

  // ========== 下载/预览（参考灵感创作：代理下载绕过 CORS） ==========
  const getProxyDownloadUrl = (sourceUrl) => getFullApiUrl() + '/v1/download?url=' + encodeURIComponent(sourceUrl);

  const downloadSingleFile = (url, filename) => {
    const token = localStorage.getItem('user_token');
    const proxyUrl = getProxyDownloadUrl(url) + '&token=' + encodeURIComponent(token);
    const a = document.createElement('a');
    a.href = proxyUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handlePreview = (item) => {
    setPreviewData({ type: 'video', url: item.videoUrl, prompt: item.prompt, record: item });
    setPreviewVisible(true);
  };

  const manualCheck = async (item) => {
    if (!item.taskId) return;
    try {
      const res = await api.get(`/v1/videos/generations/${item.taskId}?model=${encodeURIComponent(item.model || '')}`);
      const status = res?.status || '';
      const videoUrl = res?.video_url || '';
      const termSet = new Set([...TERMINAL]);
      setHistory(prev => prev.map(h => {
        if (h.taskId !== item.taskId) return h;
        const wasTerm = termSet.has(h.status);
        const newStatus = (wasTerm && !termSet.has(status)) ? h.status : status;
        return { ...h, status: newStatus, videoUrl: videoUrl || h.videoUrl };
      }));
      if (status === 'succeeded') {
        message.success(t('generation_complete_video_msg'));
        refreshHistory();
      } else if (status === 'failed') {
        message.error({ content: t('generation_failed_video') + ': ' + (upstreamErrorText(res) || t('unknown_error')), style: { textAlign: 'left' } });
      } else if (!TERMINAL.has(status)) {
        pollTask(item);
      }
    } catch (e) {
      message.error(t('check_failed') + ': ' + (e?.message || ''));
    }
  };

  const deleteHistoryItem = async (id) => {
    const item = history.find(h => h.id === id);
    if (!item) return;
    if (item.serverIds?.length) {
      await Promise.allSettled(item.serverIds.map(sid => multimodalAPI.deleteVideoHistory(sid).catch(() => {})));
    }
    if (pollTimersRef.current[item.taskId]) {
      clearTimeout(pollTimersRef.current[item.taskId]);
      delete pollTimersRef.current[item.taskId];
    }
    setHistory(prev => prev.filter(h => h.id !== id));
  };

  const clearHistory = async () => {
    Object.values(pollTimersRef.current).forEach(clearTimeout);
    pollTimersRef.current = {};
    const toDelete = history.filter(h => h.serverIds?.length);
    await Promise.allSettled(toDelete.flatMap(h => h.serverIds.map(sid => multimodalAPI.deleteVideoHistory(sid).catch(() => {}))));
    setHistory([]);
    message.success(t('cleared_all_prefix') + t('generation_history'));
  };

  const renderStatus = (status) => {
    const cfg = getStatusConfig()[status];
    if (!cfg) return <Tag>{status || t('generating_progress')}</Tag>;
    return <Tag color={cfg.color} icon={cfg.icon}>{cfg.text}</Tag>;
  };

  // 生成模式：由素材反推（首尾帧 / 参考模式 / 文生）
  const recordMode = (r) => {
    if (r.firstFrame || r.lastFrame) return t('w3_mode_keyframe');
    if (r.refImages?.length || r.refVideos?.length || r.refAudios?.length) return t('w3_mode_reference');
    return t('w3_mode_text');
  };

  const DetailRow = ({ label, children }) => (
    <Row style={{ marginBottom: 8 }}>
      <Col span={6}><Text type="secondary" style={{ fontSize: 12 }}>{label}</Text></Col>
      <Col span={18}>{children}</Col>
    </Row>
  );

  const Thumb = ({ url, label, color }) => (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <Image src={url} width={48} height={48} style={{ borderRadius: 4, objectFit: 'cover' }} preview={{ mask: <EyeOutlined /> }} />
      {label && (
        <Tag color={color} style={{ fontSize: 9, position: 'absolute', bottom: -4, left: 2, margin: 0, padding: '0 4px', lineHeight: '14px' }}>{label}</Tag>
      )}
    </div>
  );

  // 预览弹窗详情：提示词 + 素材 + 参数
  const renderPreviewDetail = (record) => {
    const p = record.params || {};
    const hasMaterials = record.firstFrame || record.lastFrame
      || record.refImages?.length || record.refVideos?.length || record.refAudios?.length;
    return (
      <div style={{ paddingTop: 4 }}>
        <DetailRow label={t('w3_detail_mode')}>
          <Tag color="purple" style={{ margin: 0 }}>{recordMode(record)}</Tag>
          <Text style={{ fontSize: 12, marginLeft: 8 }}>{record.model}</Text>
        </DetailRow>

        <DetailRow label={t('prompt_description')}>
          <Paragraph style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: 0 }}>
            {record.prompt || '-'}
          </Paragraph>
        </DetailRow>

        <DetailRow label={t('material_files')}>
          {hasMaterials ? (
            <Space size={8} wrap>
              {record.firstFrame && <Thumb url={record.firstFrame} label={t('w3_detail_first_frame')} color="green" />}
              {record.lastFrame && <Thumb url={record.lastFrame} label={t('w3_detail_last_frame')} color="orange" />}
              {record.refImages?.map((url, i) => <Thumb key={`ri-${i}`} url={url} label={`${t('w3_detail_image')}${i + 1}`} color="blue" />)}
              {record.refVideos?.map((url, i) => (
                <div key={`rv-${i}`} style={{ position: 'relative', display: 'inline-block' }}>
                  <div style={{ width: 48, height: 48, borderRadius: 4, background: '#000', overflow: 'hidden', border: '1px solid #333' }}>
                    <video src={url} preload="metadata" muted style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.6 }} />
                  </div>
                  <PlayCircleOutlined style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 16, color: '#fff' }} />
                  <Tag color="cyan" style={{ fontSize: 9, position: 'absolute', bottom: -4, left: 2, margin: 0, padding: '0 4px', lineHeight: '14px' }}>{t('w3_detail_video')}{i + 1}</Tag>
                </div>
              ))}
              {record.refAudios?.map((url, i) => (
                <a key={`ra-${i}`} href={url} target="_blank" rel="noreferrer">
                  <Tag color="purple" style={{ fontSize: 10, margin: 0, lineHeight: '20px' }}>
                    <SoundOutlined style={{ marginRight: 3 }} />{t('w3_detail_audio')}{i + 1}
                  </Tag>
                </a>
              ))}
            </Space>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>{t('no_ref_images')}</Text>
          )}
        </DetailRow>

        <DetailRow label={t('w3_detail_params')}>
          <Space size={4} wrap>
            <Tag style={{ margin: 0, fontSize: 11 }}>{t('w3_resolution')}: {record.resolution || '-'}</Tag>
            <Tag style={{ margin: 0, fontSize: 11 }}>{t('w3_ratio')}: {p.ratio || '-'}</Tag>
            <Tag style={{ margin: 0, fontSize: 11 }}>
              {t('w3_duration')}: {record.duration > 0 ? `${record.duration}s` : (p.duration === -1 ? t('w3_smart_duration_short') : '-')}
            </Tag>
            {p.audio != null && <Tag style={{ margin: 0, fontSize: 11 }} color={p.audio ? 'green' : 'default'}>{t('w3_audio')}: {p.audio ? t('yes') : t('no')}</Tag>}
            {p.prompt_extend != null && <Tag style={{ margin: 0, fontSize: 11 }}>{t('w3_prompt_extend')}: {p.prompt_extend ? t('yes') : t('no')}</Tag>}
            {p.seed != null && p.seed >= 0 && <Tag style={{ margin: 0, fontSize: 11 }}>{t('w3_seed_short')}: {p.seed}</Tag>}
          </Space>
        </DetailRow>

        {record.taskId && (
          <DetailRow label={t('task_query_id')}>
            <Text code copyable={{ text: record.taskId }} style={{ fontSize: 11 }}>{record.taskId}</Text>
          </DetailRow>
        )}
      </div>
    );
  };

  // ========== 模型卡（与灵感创作视频模型列表一致，不显示价格） ==========
  const renderModelCard = (model) => {
    const name = model.model_name || model.name;
    let feat = {};
    try { feat = JSON.parse(model.metadata || '{}'); } catch { feat = {}; }
    const isSelected = selectedModel === name;
    const tierColor = TIER_COLORS[feat.tier] || '#1677ff';
    return (
      <Card
        key={name}
        hoverable
        size="small"
        style={{
          cursor: 'pointer',
          borderColor: isSelected ? '#1677ff' : undefined,
          background: isSelected ? '#f0f5ff' : '#fff',
          marginBottom: 8,
          transition: 'all .2s',
        }}
        styles={{ body: { padding: '10px 12px' } }}
        onClick={() => setSelectedModel(name)}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <Space size={4}>
              <Text strong style={{ fontSize: 13 }}>{model.display_name || name}</Text>
              {feat.tier && <Tag color={tierColor} style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>{feat.tier}</Tag>}
            </Space>
            {feat.desc && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>{feat.desc}</Text>
              </div>
            )}
          </div>
          {isSelected && <CheckCircleOutlined style={{ color: '#1677ff', fontSize: 16 }} />}
        </div>
        {isSelected && (feat.features || []).length > 0 && (
          <div style={{ marginTop: 6 }}>
            <Space size={2} wrap>
              {feat.features.map((f, i) => (
                <Tag key={i} color="blue" style={{ fontSize: 10, margin: '1px 0', lineHeight: '16px', padding: '0 4px' }}>{f}</Tag>
              ))}
            </Space>
          </div>
        )}
      </Card>
    );
  };

  if (enabled === false) {
    return (
      <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
        <Card style={{ width: 520, textAlign: 'center' }}>
          <VideoCameraOutlined style={{ fontSize: 42, color: '#bfbfbf' }} />
          <p style={{ marginTop: 16, color: '#888' }}>{t('w3_studio_disabled')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      {/* 左：模型选择（参考灵感创作样式） */}
      <div style={{ width: 240, flexShrink: 0 }}>
        <Card
          size="small"
          title={<span><VideoCameraOutlined style={{ color: isWan3 ? '#1677ff' : '#722ed1' }} /> {t(LABEL_KEY)}</span>}
          styles={{ body: { padding: '8px 8px 0' } }}
        >
          {models.map(m => renderModelCard(m))}
          {models.length === 0 && (
            <Empty description={`暂无 ${MODEL_PREFIX} 模型`} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '16px 0' }} />
          )}
        </Card>
      </div>

      {/* 中：表单 */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Card size="small" title={t('video_creation')}>
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <div>
              <Text strong>生成模式</Text>
              <Radio.Group value={mode} onChange={e => setMode(e.target.value)} style={{ marginLeft: 12 }}>
                <Radio.Button value="text">{t('w3_mode_text')}</Radio.Button>
                <Radio.Button value="keyframe">{t('w3_mode_keyframe')}</Radio.Button>
                <Radio.Button value="reference">{t('w3_mode_reference')}</Radio.Button>
              </Radio.Group>
            </div>

            <TextArea
              rows={4}
              placeholder={t('creative_prompt_placeholder_video')}
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              maxLength={20000}
              showCount
            />

            {mode === 'keyframe' && (
              <>
                {renderImageUpload(firstFrame, setFirstFrame, t('w3_first_frame'))}
                {renderImageUpload(lastFrame, setLastFrame, t('w3_last_frame'))}
              </>
            )}

            {mode === 'reference' && (
              <>
                {renderMultiUpload(refImages, setRefImages, 'image/*', t('w3_ref_images'), meta?.maxRefImages ?? 10, <PictureOutlined />)}
                {renderMultiUpload(refVideos, setRefVideos, 'video/*', t('w3_ref_videos'), meta?.maxRefVideos ?? 5, <VideoCameraOutlined />)}
                {renderMultiUpload(refAudios, setRefAudios, 'audio/*', t('w3_ref_audios'), meta?.maxRefAudios ?? 5, <SoundOutlined />)}
              </>
            )}

            <Row gutter={12}>
              <Col span={12}>
                <Text strong>{t('w3_resolution')}</Text>
                <Select
                  style={{ width: '100%', marginTop: 6 }}
                  value={resolution}
                  onChange={setResolution}
                  options={(meta?.resolutions || []).map(r => ({ value: r.value, label: r.label || r.value }))}
                />
              </Col>
              <Col span={12}>
                <Text strong>{t('w3_ratio')}</Text>
                <Select
                  style={{ width: '100%', marginTop: 6 }}
                  value={ratio}
                  onChange={setRatio}
                  options={(meta?.ratios || ['adaptive', '16:9', '9:16', '1:1']).map(r => ({ value: r, label: r }))}
                />
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={12}>
                <Text strong>{t('w3_duration')}</Text>
                <div style={{ marginTop: 6 }}>
                  <InputNumber
                    min={2} max={30} style={{ width: '70%' }}
                    value={duration} onChange={setDuration} disabled={smartDuration}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginTop: 22 }}>
                  <Switch checked={smartDuration} onChange={setSmartDuration} />{' '}
                  <Tooltip title="模型自动决定时长，按 30 秒预扣、完成后按实际时长结算"><Text type="secondary">{t('w3_smart_duration')}</Text></Tooltip>
                </div>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={8}>
                <Text strong>{t('w3_audio')}</Text><br />
                <Switch checked={audio} onChange={setAudio} style={{ marginTop: 6 }} />
              </Col>
              <Col span={8}>
                <Text strong>{t('w3_prompt_extend')}</Text><br />
                <Switch checked={promptExtend} onChange={setPromptExtend} style={{ marginTop: 6 }} />
              </Col>
              <Col span={8}>
                <Text strong>{t('w3_seed')}</Text><br />
                <InputNumber min={-1} value={seed} onChange={setSeed} style={{ marginTop: 6, width: '100%' }} />
              </Col>
            </Row>

            {estimateCost != null && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                预估费用（{smartDuration ? '智能时长按30秒' : `${duration}秒`}预扣）: <Text strong>${estimateCost}</Text>
                {smartDuration && '，完成后按实际时长多退少补'}
              </Text>
            )}

            <Button
              type="primary" size="large" block loading={generating}
              onClick={handleGenerate}
              style={{ background: '#722ed1', borderColor: '#722ed1' }}
            >
              {t('generate_video_btn')}
            </Button>
          </Space>
        </Card>
      </div>

      {/* 右：历史记录（参考灵感创作：缩略图 + 预览 + 下载 + 删除） */}
      <div style={{ width: 360, flexShrink: 0, overflow: 'auto', maxHeight: 'calc(100vh - 140px)' }}>
        <Card
          size="small"
          title={
            <Space size={4}>
              <HistoryOutlined />
              <Text strong style={{ fontSize: 13 }}>{t('generation_history')}</Text>
              <Badge count={history.length} size="small" style={{ backgroundColor: '#1677ff' }} />
            </Space>
          }
          extra={
            history.length > 0 && (
              <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={clearHistory} style={{ fontSize: 11 }}>
                {t('clear')}
              </Button>
            )
          }
          styles={{ body: { padding: '4px 8px', overflow: 'auto' } }}
        >
          {history.length === 0 ? (
            <Empty description={t('no_history')} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '20px 0' }} />
          ) : (
            history.map(item => {
              const hasPreview = !!item.videoUrl;
              return (
                <div
                  key={item.id}
                  style={{
                    marginBottom: 6, borderRadius: 6, background: '#fff',
                    border: '1px solid #f0f0f0',
                  }}
                >
                  <div style={{ display: 'flex', gap: 10, padding: 8, alignItems: 'stretch' }}>
                    {/* 左：视频缩略图 */}
                    <div
                      onClick={() => hasPreview && handlePreview(item)}
                      title={hasPreview ? t('preview') : undefined}
                      style={{
                        width: 68, height: 68, borderRadius: 6, overflow: 'hidden', flexShrink: 0,
                        background: '#000', position: 'relative',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: '1px solid #f0f0f0',
                        cursor: hasPreview ? 'pointer' : 'default',
                      }}
                    >
                      {item.videoUrl ? (
                        <>
                          <video src={item.videoUrl} preload="metadata" muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          <div style={{
                            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'rgba(0,0,0,0.25)',
                          }}>
                            <PlayCircleOutlined style={{ fontSize: 22, color: 'rgba(255,255,255,0.9)' }} />
                          </div>
                        </>
                      ) : (
                        <PlayCircleOutlined style={{ fontSize: 26, color: '#fff', opacity: 0.85 }} />
                      )}
                    </div>

                    {/* 右：信息 */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space size={4}>
                          <Tag color="purple" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}>
                            {t('video_label')}
                          </Tag>
                          <Text type="secondary" style={{ fontSize: 10 }}>{item.model}</Text>
                          {item.manualTransferCharge > 0 && (
                            <Tooltip title={t('w3_manual_transfer_tag_tip').replace('{amount}', Number(item.manualTransferCharge).toFixed(3))}>
                              <Tag color="orange" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}>
                                {t('w3_manual_transfer_tag')} ${Number(item.manualTransferCharge).toFixed(3)}
                              </Tag>
                            </Tooltip>
                          )}
                        </Space>
                        {renderStatus(item.status)}
                      </div>

                      <Paragraph
                        ellipsis={{ rows: 2, tooltip: item.prompt }}
                        style={{ fontSize: 11, color: '#333', lineHeight: 1.4, margin: '3px 0', marginBottom: 0 }}
                      >
                        {item.prompt}
                      </Paragraph>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                        <Space size={4} style={{ minWidth: 0, flexShrink: 1, overflow: 'hidden' }}>
                          {item.createdAt && (
                            <Text type="secondary" style={{ fontSize: 10, whiteSpace: 'nowrap' }}>
                              <ClockCircleOutlined style={{ marginRight: 1 }} />{formatTimeShort(item.createdAt)}
                            </Text>
                          )}
                          {item.resolution && (
                            <Text type="secondary" style={{ fontSize: 10, color: '#8c8c8c', whiteSpace: 'nowrap', flexShrink: 0 }}>{item.resolution}</Text>
                          )}
                          {item.duration > 0 && (
                            <Text type="secondary" style={{ fontSize: 10, color: '#8c8c8c', whiteSpace: 'nowrap', flexShrink: 0 }}>{item.duration}s</Text>
                          )}
                        </Space>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
                          {item.taskId && (
                            <Tooltip title={t('manual_check')}>
                              <Button size="small" type="text" icon={<ReloadOutlined />}
                                style={{ fontSize: 11, padding: '0 2px', minWidth: 18, height: 20, color: '#1677ff' }}
                                onClick={() => manualCheck(item)} />
                            </Tooltip>
                          )}
                          {hasPreview && (
                            <Tooltip title={t('preview')}>
                              <Button size="small" type="text" icon={<EyeOutlined />}
                                style={{ fontSize: 11, padding: '0 2px', minWidth: 18, height: 20, color: '#52c41a' }}
                                onClick={() => handlePreview(item)} />
                            </Tooltip>
                          )}
                          {hasPreview && isDownloadableUrl(item.videoUrl) && (
                            <Tooltip title={t('download_video')}>
                              <Button size="small" type="text" icon={<DownloadOutlined />}
                                style={{ fontSize: 11, padding: '0 2px', minWidth: 18, height: 20, color: '#1677ff' }}
                                onClick={() => downloadSingleFile(item.videoUrl, 'video_' + item.id + '.mp4')} />
                            </Tooltip>
                          )}
                          {item.errorMsg && TERMINAL.has(item.status) && item.status !== 'succeeded' && (
                            <Tooltip title={item.errorMsg}>
                              <ExclamationCircleOutlined
                                style={{ fontSize: 14, color: '#ff4d4f', cursor: 'help', padding: '0 2px' }}
                              />
                            </Tooltip>
                          )}
                          <Tooltip title={t('delete')}>
                            <Button size="small" type="text" icon={<DeleteOutlined />}
                              style={{ fontSize: 11, padding: '0 2px', minWidth: 18, height: 20 }}
                              onClick={() => deleteHistoryItem(item.id)} />
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </Card>
      </div>

      {/* 预览弹窗 */}
      <Modal
        open={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width={860}
        title={
          <Space>
            <VideoCameraOutlined />
            <Text ellipsis style={{ maxWidth: 420 }}>{previewData?.prompt || t('preview')}</Text>
          </Space>
        }
        centered
      >
        {previewData?.type === 'video' && (
          <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
            <video key={previewData.record?.id || previewData.url} controls autoPlay style={{ width: '100%', maxHeight: '60vh', borderRadius: 8, background: '#000', display: 'block' }}>
              <source src={previewData.url} type="video/mp4" />
              {t('browser_not_support_video')}
            </video>
            {isDownloadableUrl(previewData.url) && (
              <Tooltip title={t('download_video')}>
                <Button
                  icon={<DownloadOutlined />} type="primary" ghost size="small"
                  onClick={() => downloadSingleFile(previewData.url, `video_${previewData.record?.id || 'w3'}.${getFileExt(previewData.url)}`)}
                  style={{ position: 'absolute', top: 8, right: 8, zIndex: 10, borderRadius: 6, fontSize: 12 }}
                >
                  {t('download_video')}
                </Button>
              </Tooltip>
            )}
          </div>
        )}

        {/* 可折叠详情面板：提示词 + 所用素材 + 生成参数 */}
        {previewData?.record && (
          <Collapse
            ghost
            size="small"
            defaultActiveKey={['detail']}
            style={{ marginTop: 16, background: '#fafafa', borderRadius: 8 }}
            items={[{
              key: 'detail',
              label: <Space size={4}><InfoCircleOutlined /><span>{t('detail_info')}</span></Space>,
              children: renderPreviewDetail(previewData.record),
            }]}
          />
        )}
      </Modal>
    </div>
  );
};

export default W3Studio;
